import { I18nProvider } from "@react-aria/i18n";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useNavigate } from "@tanstack/react-router";
import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { createTRPCClient, httpBatchStreamLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { RouterProvider } from "react-aria-components";
import superjson from "superjson";

import { TRPCProvider } from "@/integrations/trpc/react";
import type { TRPCRouter } from "@/integrations/trpc/router";
import { getBaseUrl } from "@/lib/url";

declare const __APP_VERSION__: string;

const getUrl = createIsomorphicFn()
  .server(() => `${getBaseUrl()}/api/trpc`)
  .client(() => "/api/trpc");

const getHeaders = createIsomorphicFn()
  .server(() => {
    const headers = getRequestHeaders();
    const h: Record<string, string> = {
      cookie: headers.get("cookie") ?? "",
    };
    const bypass =
      headers.get("x-vercel-protection-bypass") ??
      process.env.VERCEL_AUTOMATION_BYPASS_SECRET;
    if (bypass) h["x-vercel-protection-bypass"] = bypass;
    return h;
  })
  .client(() => ({}));

/**
 * Checks whether a tRPC error is a 4xx client error that should not be retried.
 * tRPC wraps HTTP status codes in TRPCClientError.data.httpStatus.
 */
function isClientError(error: unknown): boolean {
  if (
    error != null &&
    typeof error === "object" &&
    "data" in error &&
    error.data != null &&
    typeof error.data === "object" &&
    "httpStatus" in error.data &&
    typeof error.data.httpStatus === "number"
  ) {
    return error.data.httpStatus >= 400 && error.data.httpStatus < 500;
  }
  return false;
}

export const trpcClient = createTRPCClient<TRPCRouter>({
  links: [
    httpBatchStreamLink({
      transformer: superjson,
      url: getUrl(),
      headers: getHeaders,
      fetch: (url, options) =>
        fetch(url, { ...options, credentials: "include" }),
      maxURLLength: 2083,
      maxItems: 5,
    }),
  ],
});

export function getContext() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry(failureCount, error) {
          if (isClientError(error)) return false;
          return failureCount < 3;
        },
      },
      dehydrate: { serializeData: superjson.serialize },
      hydrate: { deserializeData: superjson.deserialize },
    },
  });

  const serverHelpers = createTRPCOptionsProxy({
    client: trpcClient,
    queryClient,
  });

  return {
    queryClient,
    trpc: serverHelpers,
  };
}

const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : null,
  serialize: superjson.stringify,
  deserialize: superjson.parse,
});

export function Provider({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        buster: __APP_VERSION__,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist match queries. tRPC query keys use the shape
            // [["router","procedure"], { input, type }] — if tRPC changes
            // this format, this filter will need updating.
            const key = query.queryKey;
            return (
              query.state.status === "success" &&
              Array.isArray(key[0]) &&
              key[0][0] === "matches" &&
              key[0][1] === "get"
            );
          },
        },
      }}
    >
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <RouterProvider navigate={(href) => navigate({ href })}>
          <I18nProvider locale="en-US">{children}</I18nProvider>
        </RouterProvider>
      </TRPCProvider>
    </PersistQueryClientProvider>
  );
}
